import { CLASSIC_SHOP_URL } from '../../app/constants';
import type { DialogueContent } from '../../interactions/dialogue';
import type { InspectContent } from '../../interactions/inspect';
import type { SceneDefinition } from '../scene.types';

export const comptoirInspections: Record<string, InspectContent> = {
  'comptoir.promo': {
    title: 'Ardoise des promotions',
    imageUrl: '/props/prop-ardoise.png',
    imageAlt: 'Ardoise manuscrite posée sur le comptoir',
    lines: [
      'Promotion du moment : ce que le chien a choisi reste au même prix.',
      'Deuxième mastication offerte si vous arrivez à en garder une entière.',
      'Le rayon Friandises se réapprovisionne dès que le suspect se réveille.',
    ],
  },
};

export const comptoirDialogues: DialogueContent[] = [
  {
    id: 'comptoir.entry',
    lines: [
      'Bonjour. Je peux vous renseigner, mais je préfère prévenir : ici, les chiens ont souvent déjà choisi.',
    ],
    character: 'Le vendeur',
    once: true,
  },
  {
    id: 'comptoir.bell',
    lines: [
      'Ding. Le vendeur arrive. Il tenait déjà votre sac.',
      'Ding. Quelqu’un répond « deux minutes » depuis la réserve.',
    ],
  },
  {
    id: 'comptoir.promo',
    lines: ['Ardoise : « Promotion du moment : ce que le chien a choisi reste au même prix. »'],
  },
  {
    id: 'comptoir.reward',
    lines: [
      'Vous avez retrouvé les trois os disparus. L’enquête est officiellement close. Le suspect dort sous le comptoir.',
    ],
    character: 'Le vendeur',
  },
  {
    id: 'comptoir.vendeur',
    lines: [
      'Je peux vous conseiller, mais votre chien a déjà un avis très arrêté.',
      'Non, nous ne reprenons pas les mastications entamées. Cela s’est déjà posé.',
      'Le responsable du rayon est parti poursuivre un pigeon. Il revient en général.',
    ],
    character: 'Le vendeur',
  },
  {
    id: 'comptoir.dog',
    lines: [
      'Sous le comptoir, un chien dort profondément, entouré de miettes compromettantes.',
      'Il ouvre un œil, vous regarde, et décide que non.',
    ],
  },
];

export const comptoirScene: SceneDefinition = {
  id: 'comptoir',
  title: 'Le Comptoir',
  background: '/scenes/scene-comptoir-background-v2.webp',
  entryDialogueId: 'comptoir.entry',
  layers: [
    {
      /*
       * Le conseiller se tient DERRIÈRE le comptoir, entre les deux produits
       * posés dessus, et ses pattes reposent sur le plateau.
       *
       * Relevé sur le fond, dans la bande centrale du comptoir :
       *   63,9 % à 65,6 %  face supérieure du plateau blanc, éclairée
       *   66,1 % à 71,7 %  tranche du plateau, vue de face
       *   75 %   à 89 %    socle en bois
       *
       * La base du prop doit donc tomber sur la FACE SUPÉRIEURE, à 65 %. Une
       * base à 68 % le posait contre la tranche, c'est-à-dire sous le plateau.
       *
       * La hauteur découle de la largeur : le prop fait 623 x 786, la scène est
       * en 16/9, donc une largeur de L % occupe 2,242 x L % de la hauteur.
       * 12,5 % de large valent 28 % de haut, d'où y = 65 - 28.
       */
      id: 'comptoir-conseiller',
      src: '/props/prop-conseiller.webp',
      depth: 0.35,
      alt: 'Le conseiller de la boutique, un chien en tenue de maison',
      x: 43.5,
      y: 37,
      width: 12.5,
    },
    /*
     * Objets posés SUR le plateau du comptoir, dont la face supérieure
     * commence à 63,9 % de la hauteur de scène. On vise donc une base à 65 %,
     * comme le conseiller.
     *
     * La hauteur d'un prop se déduit de sa largeur et de son rapport d'image,
     * la scène étant en 16/9 :
     *   ardoise   400 x 490 -> 2,178 x la largeur ; 9 % de large = 19,6 % de haut
     *   sonnette  412 x 389 -> 1,679 x la largeur ; 5 % de large =  8,4 % de haut
     */
    { id: 'comptoir-ardoise', src: '/props/prop-ardoise.png', depth: 0.3, decorative: true, x: 67, y: 45.4, width: 9 },
    { id: 'comptoir-sonnette', src: '/props/prop-sonnette.png', depth: 0.5, decorative: true, x: 23, y: 56.6, width: 5 },
    { id: 'comptoir-chien', src: '/props/prop-chien-endormi.png', depth: 0.75, decorative: true, x: 30, y: 84, width: 14 },
  ],
  // Le mur d'étagères derrière le comptoir présente les meilleures ventes.
  featured: {
    positions: [
      { x: 27, y: 12, width: 9, height: 9 },
      { x: 38, y: 12, width: 9, height: 9 },
      { x: 49, y: 12, width: 9, height: 9 },
      { x: 60, y: 12, width: 9, height: 9 },
    ],
  },
  hotspots: [
    {
      id: 'comptoir.bell',
      label: 'Appuyer sur la sonnette du comptoir',
      action: 'animation',
      target: 'comptoir.bell',
      x: 22,
      y: 52,
      width: 9,
      height: 11,
      hint: 'La sonnette',
    },
    {
      id: 'comptoir.promo',
      label: 'Lire l’ardoise des promotions',
      action: 'inspect',
      target: 'comptoir.promo',
      x: 66,
      y: 50,
      width: 12,
      height: 13,
      hint: 'L’ardoise',
    },
    {
      id: 'comptoir.reward',
      label: 'Demander la récompense de l’enquête',
      action: 'dialogue',
      target: 'comptoir.reward',
      x: 52,
      y: 30,
      width: 13,
      height: 26,
      hint: 'L’enquête',
      simplifiedLabel: 'Demander la récompense de la mini-quête',
    },
    {
      id: 'comptoir.classic-shop',
      label: 'Ouvrir la boutique classique Chien Heureux',
      action: 'external-link',
      target: CLASSIC_SHOP_URL,
      x: 13,
      y: 14,
      width: 11,
      height: 62,
      hint: 'Boutique classique',
      simplifiedLabel: 'Ouvrir la boutique classique',
    },
    {
      id: 'comptoir.vendeur',
      label: 'Demander conseil au vendeur',
      action: 'animation',
      target: 'comptoir.vendeur',
      // La zone cliquable suit le conseiller : même cadre que sa couche.
      x: 43.5,
      y: 37,
      width: 12.5,
      height: 28,
      hint: 'Le vendeur',
      simplifiedLabel: 'Demander conseil au vendeur',
    },
    {
      id: 'comptoir.cart',
      label: 'Ouvrir mon panier',
      action: 'animation',
      target: 'comptoir.bell',
      x: 48,
      y: 54,
      width: 12,
      height: 11,
      hint: 'Le panier',
      simplifiedLabel: 'Ouvrir mon panier',
    },
    {
      id: 'comptoir.dog',
      label: 'Regarder sous le comptoir',
      action: 'animation',
      target: 'comptoir.dog',
      x: 30,
      y: 86,
      width: 16,
      height: 12,
      hint: 'Sous le comptoir',
      simplifiedLabel: 'Regarder sous le comptoir',
    },
    {
      id: 'comptoir.product-1',
      label: 'Regarder le bâton de fromage dur',
      action: 'product',
      target: 'baton-de-fromage-chien',
      x: 34,
      y: 62,
      width: 8,
      height: 10,
      hint: 'Le fromage',
      simplifiedLabel: 'Voir le bâton de fromage dur',
    },
    {
      id: 'comptoir.product-2',
      label: 'Regarder les friandises de dressage',
      action: 'product',
      target: 'friandise-de-dressage-boeuf',
      x: 58,
      y: 62,
      width: 8,
      height: 10,
      hint: 'Le dressage',
      simplifiedLabel: 'Voir les friandises de dressage',
    },
    {
      id: 'comptoir.to-reserve',
      label: 'Entrer dans la réserve',
      action: 'navigate',
      target: 'reserve',
      x: 80,
      y: 12,
      width: 13,
      height: 62,
      hint: 'La réserve',
      simplifiedLabel: 'Entrer dans la réserve',
    },
    {
      id: 'comptoir.to-hall',
      label: 'Retourner au Hall',
      action: 'navigate',
      target: 'hall',
      x: 2,
      y: 40,
      width: 9,
      height: 34,
      hint: 'Retour au Hall',
      simplifiedLabel: 'Retourner au Hall',
    },
  ],
};
